'use client';
import { Grid, IconButton, Paper, Tab, Tabs, Tooltip } from '@mui/material';
import Link from 'next/link';
import { useSelectedLayoutSegments } from 'next/navigation';
import { LinkPendingIcon, MaterialIcon } from '../../components';
import { MaterialIconType } from '../../components/materialIcon/types';
import { COLORS, MOBILE_SHEET_SX } from '../../constants';

export type TabTableLayoutLink = {
  title: string;
  testid?: string;
  label: string;
  icon: MaterialIconType;
  action?: () => void;
};
export type TabTableLayoutActions = {
  onClick: () => void;
  label: string;
  icon: MaterialIconType;
};
type TabTableLayoutProps = {
  children: React.ReactNode;
  links?: TabTableLayoutLink[];
  actions?: TabTableLayoutActions[];
  activeSegment?: string;
  // Below md, round only the top corners, for pages where the panel runs edge to edge down to the screen bottom
  fullBleedMobile?: boolean;
  // Below md, no tabs row: for apps whose phone tab bar already switches between the links
  hideTabsOnMobile?: boolean;
};

export const TabTableLayout = ({
  children,
  links,
  actions,
  activeSegment,
  fullBleedMobile,
  hideTabsOnMobile,
}: TabTableLayoutProps) => {
  const segment = useSelectedLayoutSegments();
  const activeIndex = links?.findIndex((link) => link.title === (activeSegment || segment[0])) ?? -1;
  // Route links follow MUI's "navigation tabs" pattern; tabs driven by `action` stay as in-page buttons
  const isNavigation = !!links?.length && links.every((link) => !link.action);

  return (
    <Paper
      sx={{
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        flex: 1,
        overflowX: 'auto',
        // overflowX alone turns overflowY to auto too: a few stray pixels (focused tab, ripples) then made the panel
        // its own vertical scroller, so a swipe moved the panel instead of the page
        overflowY: 'hidden',
        // Below md: a sheet rising from the background, rounded on top and square where it meets the screen bottom
        ...(fullBleedMobile && { borderRadius: MOBILE_SHEET_SX.borderRadius }),
      }}
      data-testid="tabtable-layout"
    >
      <Grid
        container
        sx={{
          display: { xs: hideTabsOnMobile ? 'none' : 'flex', md: 'flex' },
          borderBottom: 1,
          borderColor: COLORS.background[1],
          flexDirection: 'row',
          flexWrap: 'nowrap',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Tabs
          value={activeIndex === -1 ? false : activeIndex}
          role={isNavigation ? 'navigation' : undefined}
          aria-label="Secciones"
        >
          {links?.map(({ title, label, icon, testid, action }, ix) =>
            action ? (
              <Tab
                data-testid={testid}
                iconPosition="start"
                icon={<MaterialIcon icon={icon} pointer />}
                key={title}
                label={label}
                onClick={action}
              />
            ) : (
              <Tab
                component={Link}
                href={title}
                aria-current={ix === activeIndex ? 'page' : undefined}
                data-testid={testid}
                iconPosition="start"
                icon={<LinkPendingIcon icon={icon} />}
                key={title}
                label={label}
              />
            ),
          )}
        </Tabs>
        <Grid
          container
          sx={{
            gap: 1,
            px: 1,
          }}
        >
          {actions?.map(({ onClick, label, icon }) => (
            <Tooltip key={label} title={label}>
              <IconButton onClick={onClick}>
                <MaterialIcon icon={icon} />
              </IconButton>
            </Tooltip>
          ))}
        </Grid>
        {/* <IconButton onClick={() => router.back()}>
            <MaterialIcon icon="download" />
          </IconButton> */}
      </Grid>
      {children}
    </Paper>
  );
};
